import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useReducer,
  useRef,
} from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import _debounce from "lodash/debounce";
import { FoodSearchResults } from "./FoodSearchResults";
import SaveInputButtons from "../../../../../parts/SaveInputButtons";
import {
  completeVolumeUnits,
  completeWeightUnits,
  weightUnits,
  calculateMeasurementPerUnit,
  getFilteredFoods,
} from "../../../../../utils/nutrition-util";
import Select from "../../../../../parts/inputs/select";
import { getItemsToAdd } from "../../../../../utils/nutrition-helpers";
import { InsertedContainerWithAnimations } from "./InsertedContainerWithAnimations";
import { capitalizeFirstLetter } from "../../../../../utils/string-helpers";

export const FoodActions = forwardRef(
  (
    {
      locals,
      searchFilter,
      api,
      onAddItem,
      foodActionState,
      setFoodActionState,
    },
    ref
  ) => {
    const { t } = useTranslation(["athlete-portal", "common"]);
    const { customFoods = [], activeUserToRequest } = locals;
    const { mealPlanDataSources = ["usda", "afcd", "verified-food"] } = activeUserToRequest;
    const searchContainerRef = useRef(null);
    const quantityInputRef = useRef(null);

    const initialState = {
      isLoading: false,
      error: null,
      quantity: 0,
      show: false,
      hideAnimation: false,
      genericFoods: [],
      hasMore: false,
      nextVerifiedOffset: 0,
      nextGeneralOffset: 0,
      isLoadingMore: false,
    };

    /**
     * Update the state of this component
     * @param {*} state
     * @param {*} action
     * @returns
     */
    const foodActionsReducer = (state, action) => {
      switch (action.type) {
        case "UPDATE_STATE":
          return { ...state, ...action.payload };
        case "RESET":
          return initialState;
        case "RESET_WITHOUT_PROPERTY":
          const { keys } = action.payload;
          const newState = { ...initialState };

          keys.forEach((key) => {
            delete newState[key];
          });

          keys.forEach((key) => {
            if (state[key] !== undefined) {
              newState[key] = state[key];
            }
          });

          return newState;
        default:
          return state;
      }
    };

    const [foodState, dispatch] = useReducer(foodActionsReducer, initialState);

    const {
      isLoading,
      error,
      quantity,
      show,
      hideAnimation,
      genericFoods,
      hasMore,
      nextVerifiedOffset,
      nextGeneralOffset,
      isLoadingMore,
    } = foodState;

    const {
      isSearching,
      selectedItem,
      unitOfMeasure,
      unitOfMeasureOptions,
      searchValue,
    } = foodActionState;

    //Send the method to the parent component
    useImperativeHandle(ref, () => ({
      focusQuantityInput: () => {
        quantityInputRef.current?.focus();
        quantityInputRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      },
    }));

    /**
     * Filter the custom food data
     */

    const data = useMemo(() => {
      const filterMap = {
        all: null,
        recipes: "recipe",
        meals: "meal",
        foods: "food",
      };

      const filterFn = ({ name, type }) => {
        const matchesSearch = searchValue
          ? name.toLowerCase().includes(searchValue.toLowerCase())
          : true;

        const targetType = filterMap[searchFilter];
        const matchesType = targetType
          ? type === targetType || (targetType === "food" && type === "verified-food")
          : true;

        return matchesSearch && matchesType;
      };

      const filteredCustom = customFoods.filter(filterFn);
      if (searchFilter === "all") {
        const order = ["food", "recipe", "meal"];
        const orderedCustom = order.flatMap((t) =>
          filteredCustom.filter((f) => f.type === t)
        );

        return [...orderedCustom, ...(genericFoods || [])];
      }
      return [...filteredCustom, ...(genericFoods || [])];
    }, [searchValue, searchFilter, customFoods, genericFoods]);

    /**
     * UseEffect to close the search icons if clicked outside
     */
    useEffect(() => {
      const handleClickOutside = (event) => {
        if (
          searchContainerRef.current &&
          !searchContainerRef.current.contains(event.target)
        ) {
          updateStateFromAddFoodModal({ isSearching: false });
        }
      };

      document.addEventListener("mousedown", handleClickOutside);

      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, []);

    /**
     * Reset the state if the user changed the tab
     */
    useEffect(() => {
      dispatch({
        type: "RESET_WITHOUT_PROPERTY",
        payload: { keys: ["show", "hideAnimation"] },
      });

      updateStateFromAddFoodModal({
        unitOfMeasure: "Select",
        unitOfMeasureOptions: [],
        selectedItem: null,
        isSearching: false,
      });
    }, [searchFilter]);

    /**
     * Hide the content of the animation
     */
    const handleHideAnimation = () => {
      dispatch({ type: "UPDATE_STATE", payload: { hideAnimation: true } });

      setTimeout(() => {
        dispatch({ type: "UPDATE_STATE", payload: { show: false } });

        setTimeout(() => {
          dispatch({ type: "UPDATE_STATE", payload: { hideAnimation: false } });
        }, 50); //A little delay to be sure to do a right re-render
      }, 1000); // Animation time
    };

    /**
     * Function to avoid strings in number input
     * @param {*} e
     */
    const handleKeyDown = (e) => {
      if (["e", "E", "-", "+", ","].includes(e.key)) {
        e.preventDefault();
      }
    };

    /**
     * Function to update the state from AddFoodModal
     * @param {*} newState
     * @returns
     */
    const updateStateFromAddFoodModal = (newState) =>
      setFoodActionState((prevState) => ({ ...prevState, ...newState }));

    /**
     * Handle search when the user is typing
     */

    const debouncedSearch = useRef(
      _debounce(async (searchValue) => {
        if (!mealPlanDataSources || mealPlanDataSources.length === 0) {
          return;
        }

        dispatch({
          type: "UPDATE_STATE",
          payload: { isLoading: true, error: null },
        });

        try {
          const result = await getFilteredFoods(
            searchValue,
            mealPlanDataSources,
            api
          );
          dispatch({
            type: "UPDATE_STATE",
            payload: {
              genericFoods: result.data,
              hasMore: result.hasMore,
              nextVerifiedOffset: result.nextVerifiedOffset,
              nextGeneralOffset: result.nextGeneralOffset,
              isLoading: false,
            },
          });
        } catch (err) {
          console.error(err);
          dispatch({
            type: "UPDATE_STATE",
            payload: {
              isLoading: false,
              error: t("athlete-portal:foodActions.failedToFetch"),
            },
          });
        }
      }, 1000)
    );

    /**
     * Debounce handleSearch
     */

    useEffect(() => {
      if (
        searchValue.length > 1 &&
        (searchFilter === "all" || searchFilter === "foods")
      ) {
        debouncedSearch.current(searchValue);
      } else {
        if (foodState.genericFoods.length > 0) {
          dispatch({
            type: "UPDATE_STATE",
            payload: {
              genericFoods: [],
              hasMore: false,
              nextVerifiedOffset: 0,
              nextGeneralOffset: 0,
            },
          });
        }
      }
    }, [searchValue, searchFilter]);

    /**
     * Cleanup function to cancel the debounce when the component unmounts
     */
    useEffect(() => {
      return () => {
        if (debouncedSearch.current) {
          debouncedSearch.current.cancel();
        }
      };
    }, []);

    /**
     * Reset the search value
     * @param {*} param0
     */
    const handleResetInputSearch = (e) => {
      e.preventDefault();
      dispatch({
        type: "RESET_WITHOUT_PROPERTY",
        payload: { keys: ["show", "quantity", "hideAnimation"] },
      });
      updateStateFromAddFoodModal({
        unitOfMeasureOptions: [],
        isSearching: false,
        searchValue: "",
      });

      setTimeout(() => {
        updateStateFromAddFoodModal({
          selectedItem: null,
          unitOfMeasure: "Select",
        });
        dispatch({ type: "UPDATE_STATE", payload: { quantity: 0 } });
      }, 1000);
    };

    /**
     * submit the search
     * @param {*} param0
     */
    const handleSearch = async (e) => {
      e.preventDefault();
      if (
        (searchFilter === "all" || searchFilter === "foods") &&
        mealPlanDataSources.length > 0
      ) {
        dispatch({
          type: "UPDATE_STATE",
          payload: { isLoading: true, error: null },
        });
        try {
          const result = await getFilteredFoods(
            searchValue,
            mealPlanDataSources,
            api
          );
          dispatch({
            type: "UPDATE_STATE",
            payload: {
              genericFoods: result.data,
              hasMore: result.hasMore,
              nextVerifiedOffset: result.nextVerifiedOffset,
              nextGeneralOffset: result.nextGeneralOffset,
              isLoading: false,
            },
          });
        } catch (err) {
          console.error(err);
          dispatch({
            type: "UPDATE_STATE",
            payload: {
              isLoading: false,
              error: t("athlete-portal:foodActions.failedToFetch"),
            },
          });
        }
      }
    };

    /**
     * Load more generic search results (pagination)
     */
    const handleLoadMore = async () => {
      if (!hasMore || isLoadingMore) return;
      dispatch({
        type: "UPDATE_STATE",
        payload: { isLoadingMore: true },
      });
      try {
        const result = await getFilteredFoods(
          searchValue,
          mealPlanDataSources,
          api,
          {
            verifiedOffset: nextVerifiedOffset,
            generalOffset: nextGeneralOffset,
          }
        );
        dispatch({
          type: "UPDATE_STATE",
          payload: {
            genericFoods: [...genericFoods, ...result.data],
            hasMore: result.hasMore,
            nextVerifiedOffset: result.nextVerifiedOffset,
            nextGeneralOffset: result.nextGeneralOffset,
            isLoadingMore: false,
          },
        });
      } catch (err) {
        console.error(err);
        dispatch({
          type: "UPDATE_STATE",
          payload: { isLoadingMore: false },
        });
      }
    };

    /**
     * Select the item from the search bar
     * @param {*} item
     */
    const handleItemSelect = (item) => {
      const {
        unitOfMeasure,
        quantityDescription,
        name,
        quantityNumber,
        measurementsValue,
      } = item;

      const unitOfMeasureForDefaultMeasure = unitOfMeasure || "g";

      const defaultMeasure = {
        value: "custom",
        text: `${quantityDescription} - ${calculateMeasurementPerUnit(
          quantityNumber,
          measurementsValue
        )} ${unitOfMeasureForDefaultMeasure}`,
      };

      let optionsToSelect = !unitOfMeasure
        ? [defaultMeasure]
        : weightUnits.includes(unitOfMeasure)
        ? [defaultMeasure, ...completeWeightUnits]
        : [defaultMeasure, ...completeVolumeUnits];

      updateStateFromAddFoodModal({
        unitOfMeasureOptions: optionsToSelect,
        unitOfMeasure: defaultMeasure.value,
        isSearching: false,
        selectedItem: item,
        searchValue: name || "",
      });
    };

    /**
     * Function to add a food
     */
    const handleAdd = (e) => {
      if (selectedItem) {
        dispatch({ type: "UPDATE_STATE", payload: { show: true } });
        //we are delaying the add step, for the animation
        const itemsToAdd = getItemsToAdd(selectedItem, quantity, unitOfMeasure, true);
        setTimeout(() => {
          onAddItem(itemsToAdd);
        }, 1000);

        handleResetInputSearch(e);
      }
    };

    /**
     * Select the unit of measure
     * @param {*} newVal
     */
    const handleSelectChange = (newVal) => {
      updateStateFromAddFoodModal({ unitOfMeasure: newVal });
    };

    return (
      <div className="second-row">
        <div className="title">
          <span className="font-bold">
            {t("athlete-portal:foodActions.addFilter", {
              filter:
                searchFilter === "all"
                  ? "Foods"
                  : capitalizeFirstLetter(searchFilter),
            })}
          </span>
          {searchFilter === "recipes" && (
            <div className="chip info widget-info">
              {t("athlete-portal:foodActions.createRecipesWidget")}
            </div>
          )}
        </div>
        <div className="inputs-container">
          <div className="w-full" ref={searchContainerRef}>
            <div className="step">
              <span className="text">
                {t("athlete-portal:foodActions.searchAndAddFilter", {
                  filter:
                    searchFilter === "all" ? "foods" : searchFilter,
                })}
              </span>
            </div>
            <div className="search-container">
              <input
                onFocus={() =>
                  updateStateFromAddFoodModal({ isSearching: true })
                }
                className={
                  isSearching
                    ? "w-full! input-search pr-22!"
                    : "w-full! input-search"
                }
                placeholder={t("athlete-portal:foodActions.searchFoodPlaceholder")}
                value={searchValue}
                onChange={({ target: { value } }) => {
                  updateStateFromAddFoodModal({
                    searchValue: value,
                  });
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    handleSearch(e);
                  }
                }}
                type="search"
              />
              <SaveInputButtons
                enabled={isSearching}
                handleSave={(e) => {
                  handleSearch(e);
                }}
                handleDismiss={(e) => handleResetInputSearch(e)}
                saveDisabled={!searchValue || selectedItem}
                actionText={t("athlete-portal:foodActions.actionSearch")}
                showIcon={true}
              />
            </div>
            {isSearching && searchValue.length > 1 && (
              <FoodSearchResults
                isLoading={isLoading}
                data={data}
                error={error}
                onItemSelect={handleItemSelect}
                hasMore={
                  (searchFilter === "all" || searchFilter === "foods") && hasMore
                }
                isLoadingMore={isLoadingMore}
                onLoadMore={
                  (searchFilter === "all" || searchFilter === "foods")
                    ? handleLoadMore
                    : undefined
                }
              />
            )}
          </div>

          {(searchFilter === "foods" ||
            (searchFilter === "all" &&
              (!selectedItem ||
                selectedItem?.type === "food" ||
                selectedItem?.type === "verified-food"))) && (
            <div className={unitOfMeasure !== "Select" ? "opacity-text" : ""}>
              <div className="step">
                <span className="text">{t("athlete-portal:foodActions.servingSize")}</span>
              </div>
              <Select
                withTruncate
                onChange={(newVal) => handleSelectChange(newVal)}
                name="unitOfMeasure"
                disabled={unitOfMeasureOptions.length < 2}
                value={unitOfMeasure}
                options={unitOfMeasureOptions.map(({ value, text }) => {
                  return { value, text };
                })}
              />
            </div>
          )}

          {searchFilter && (
            <div>
              <div className="step">
                <span className="text">{t("athlete-portal:foodActions.numberOfServings")}</span>
              </div>
              <input
                ref={quantityInputRef}
                onKeyDown={handleKeyDown}
                value={quantity || ""}
                onChange={({ target: { value } }) => {
                  //Allow only 2 decimals
                  if (/^\d*\.?\d{0,2}$/.test(value)) {
                    dispatch({
                      type: "UPDATE_STATE",
                      payload: { quantity: value },
                    });
                  }
                }}
                type="number"
                placeholder={t("athlete-portal:foodActions.servingsPlaceholder")}
              />
            </div>
          )}
          <div className="button-container">
            <button
              data-button-type={"outline"}
              onClick={handleAdd}
              disabled={!selectedItem || quantity <= 0}
            >
              <span>{t("common:add")}</span>
            </button>
          </div>
        </div>
        {show && (
          <InsertedContainerWithAnimations
            onEnd={handleHideAnimation}
            hideAnimation={hideAnimation}
            selectedItem={selectedItem}
            quantity={quantity}
            unitOfMeasure={unitOfMeasure}
          />
        )}
      </div>
    );
  }
);

FoodActions.propTypes = {
  locals: PropTypes.object,
  searchFilter: PropTypes.string,
  api: PropTypes.any,
  onAddItem: PropTypes.func,
  foodActionState: PropTypes.object,
  setFoodActionState: PropTypes.func,
};
